/**
 * skewTUtils.ts
 *
 * Meteorological calculations and coordinate transformations for Skew-T log-P diagrams.
 * Adheres to standard meteorological standards (Bolton 1980 dew point, log-P pressure,
 * skewed temperature isotherms, and thermodynamic reference lines).
 */

export interface SkewTPoint {
  timestamp: number; // Unix timestamp in ms
  pressure: number;  // hPa
  temperature: number; // °C
  dewpoint?: number;   // °C (if RH was available)
  relativeHumidity?: number; // % (0-100)
  altitude?: number;   // GNSS altitude in meters (if available)
}

export interface SkewTOptions {
  plotLeft: number;
  plotTop: number;
  plotWidth: number;
  plotHeight: number;
  pMin: number; // Top pressure (default 100 hPa)
  pMax: number; // Bottom pressure (default 1050 hPa)
  tMin: number; // Minimum temperature at bottom axis (°C, e.g. -40)
  tMax: number; // Maximum temperature at bottom axis (°C, e.g. +40)
  skewAngleDeg?: number; // Skew angle in degrees (default 45°)
}

/**
 * Calculates dew point temperature (°C) from temperature (°C) and relative humidity (%)
 * using Bolton (1980) approximation, matching MetPy's `dewpoint_from_relative_humidity`.
 */
export function calculateDewpoint(tempC: number, rhPercent: number): number | undefined {
  if (isNaN(tempC) || isNaN(rhPercent)) return undefined;
  // Clamp RH to valid range [0.1, 100] to prevent log errors
  const rh = Math.max(0.1, Math.min(100, rhPercent));
  
  // Saturation vapor pressure (hPa) - Bolton (1980)
  const es = 6.112 * Math.exp((17.67 * tempC) / (tempC + 243.5));
  // Vapor pressure (hPa)
  const e = (rh / 100) * es;
  
  if (e <= 0) return undefined;
  
  // Inverse formula for dewpoint
  const logE = Math.log(e / 6.112);
  const denom = 17.67 - logE;
  if (Math.abs(denom) < 1e-6) return undefined;
  
  const td = (243.5 * logE) / denom;
  return parseFloat(td.toFixed(2));
}

/**
 * Maps atmospheric pressure (hPa) to screen Y coordinate on a logarithmic scale.
 * Note: Lower pressure (higher altitude) maps towards the top (smaller Y).
 */
export function pressureToY(p: number, options: SkewTOptions): number {
  const { plotTop, plotHeight, pMin, pMax } = options;
  const clampedP = Math.max(pMin, Math.min(pMax, p));
  // Normalized Y: 0 at pMax (bottom), 1 at pMin (top)
  const yNorm = (Math.log(pMax) - Math.log(clampedP)) / (Math.log(pMax) - Math.log(pMin));
  return plotTop + (1 - yNorm) * plotHeight;
}

/**
 * Maps screen Y coordinate back to atmospheric pressure (hPa).
 */
export function yToPressure(y: number, options: SkewTOptions): number {
  const { plotTop, plotHeight, pMin, pMax } = options;
  const yNorm = 1 - (y - plotTop) / plotHeight;
  const clampedNorm = Math.max(0, Math.min(1, yNorm));
  return Math.exp(Math.log(pMax) - clampedNorm * (Math.log(pMax) - Math.log(pMin)));
}

/**
 * Maps temperature (°C) and pressure (hPa) to screen X coordinate on a skewed axis.
 * In a Skew-T diagram, isotherms slant upwards and to the right at angle alpha.
 */
export function temperatureToX(tempC: number, p: number, options: SkewTOptions): number {
  const { plotLeft, plotWidth, plotHeight, pMin, pMax, tMin, tMax, skewAngleDeg = 45 } = options;
  const clampedP = Math.max(pMin, Math.min(pMax, p));
  const yNorm = (Math.log(pMax) - Math.log(clampedP)) / (Math.log(pMax) - Math.log(pMin));
  
  // Base X at bottom pressure pMax
  const xBase = plotLeft + ((tempC - tMin) / (tMax - tMin)) * plotWidth;
  
  // Skew displacement to the right proportional to vertical height
  const angleRad = (skewAngleDeg * Math.PI) / 180;
  const skewOffset = yNorm * plotHeight * Math.tan(angleRad);
  
  return xBase + skewOffset;
}

/**
 * Maps screen X and Y coordinates back to physical temperature (°C).
 */
export function xyToTemperature(x: number, y: number, options: SkewTOptions): number {
  const { plotLeft, plotWidth, plotHeight, tMin, tMax, skewAngleDeg = 45 } = options;
  const yNorm = 1 - (y - options.plotTop) / plotHeight;
  const angleRad = (skewAngleDeg * Math.PI) / 180;
  const skewOffset = yNorm * plotHeight * Math.tan(angleRad);
  
  const xBase = x - skewOffset;
  return tMin + ((xBase - plotLeft) / plotWidth) * (tMax - tMin);
}

/**
 * Interpolates between two hex colors based on a factor t in [0, 1].
 */
export function interpolateTwoColors(colorStart: string, colorEnd: string, t: number): string {
  const factor = Math.max(0, Math.min(1, t));
  
  const parseHex = (hex: string) => {
    const clean = hex.replace('#', '');
    const num = parseInt(clean, 16);
    if (clean.length === 3) {
      const r = ((num >> 8) & 0xf) * 17;
      const g = ((num >> 4) & 0xf) * 17;
      const b = (num & 0xf) * 17;
      return [r, g, b];
    }
    return [(num >> 16) & 0xff, (num >> 8) & 0xff, num & 0xff];
  };

  const [r1, g1, b1] = parseHex(colorStart);
  const [r2, g2, b2] = parseHex(colorEnd);

  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);

  return `rgb(${r}, ${g}, ${b})`;
}

// ── Thermodynamic Reference Line Generators ───────────────────────────────────

export interface IsobarLine {
  pressure: number;
  y: number;
  label: string;
}

export const STANDARD_ISOBARS = [1000, 925, 850, 700, 600, 500, 400, 300, 250, 200, 150, 100];

export function getIsobars(options: SkewTOptions): IsobarLine[] {
  const { pMin, pMax } = options;
  const delta = pMax - pMin;

  let pressures: number[];
  if (delta <= 150) {
    const step = 25;
    const start = Math.ceil(pMin / step) * step;
    pressures = [];
    for (let p = start; p <= pMax; p += step) {
      pressures.push(p);
    }
  } else if (delta <= 350) {
    const step = 50;
    const start = Math.ceil(pMin / step) * step;
    pressures = [];
    for (let p = start; p <= pMax; p += step) {
      pressures.push(p);
    }
  } else if (delta <= 650) {
    const step = 100;
    const start = Math.ceil(pMin / step) * step;
    pressures = [];
    for (let p = start; p <= pMax; p += step) {
      pressures.push(p);
    }
  } else {
    pressures = STANDARD_ISOBARS.filter(p => p >= pMin && p <= pMax);
  }

  // Deduplicate and sort descending (highest pressure first)
  pressures = Array.from(new Set(pressures)).sort((a, b) => b - a);

  return pressures.map(p => ({
    pressure: p,
    y: pressureToY(p, options),
    label: `${p} hPa`
  }));
}

export interface IsothermLine {
  temperature: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  isFreezing: boolean;
}

export function getIsotherms(options: SkewTOptions, step: number = 10): IsothermLine[] {
  const { tMin, tMax, pMin, pMax } = options;
  // Span wide enough to cover the skewed area
  const startT = Math.floor((tMin - 60) / step) * step;
  const endT = Math.ceil((tMax + 60) / step) * step;

  const isotherms: IsothermLine[] = [];
  for (let t = startT; t <= endT; t += step) {
    const x1 = temperatureToX(t, pMax, options);
    const y1 = pressureToY(pMax, options);
    const x2 = temperatureToX(t, pMin, options);
    const y2 = pressureToY(pMin, options);

    isotherms.push({
      temperature: t,
      x1,
      y1,
      x2,
      y2,
      isFreezing: t === 0
    });
  }
  return isotherms;
}

export interface AdiabatCurve {
  thetaK: number;
  points: Array<{ x: number; y: number }>;
}

/**
 * Dry adiabats: lines of constant potential temperature theta (Kelvin).
 * T(p) = theta * (p / 1000)^0.286 - 273.15
 */
export function getDryAdiabats(options: SkewTOptions): AdiabatCurve[] {
  const thetas = [250, 270, 290, 310, 330, 350, 370, 390, 410];
  const { pMin, pMax } = options;
  const pStep = Math.max(5, Math.round((pMax - pMin) / 30));

  return thetas.map(theta => {
    const points: Array<{ x: number; y: number }> = [];
    for (let p = pMax; p >= pMin; p -= pStep) {
      const tempC = theta * Math.pow(p / 1000, 0.286) - 273.15;
      points.push({
        x: temperatureToX(tempC, p, options),
        y: pressureToY(p, options)
      });
    }
    return { thetaK: theta, points };
  });
}

/**
 * Moist adiabats: saturated pseudo-adiabatic lapse rate curves.
 */
export function getMoistAdiabats(options: SkewTOptions): AdiabatCurve[] {
  const startThetas = [280, 290, 300, 310, 320, 330];
  const { pMin, pMax } = options;
  const dp = Math.max(5, Math.round((pMax - pMin) / 30));

  const Rd = 287;
  const cp = 1005;
  const eps = 0.622;
  const Lv = 2.501e6;

  return startThetas.map(theta => {
    const points: Array<{ x: number; y: number }> = [];
    let currentT = theta * Math.pow(pMax / 1000, 0.286) - 273.15;

    for (let p = pMax; p >= pMin; p -= dp) {
      points.push({
        x: temperatureToX(currentT, p, options),
        y: pressureToY(p, options)
      });

      // Compute moist adiabatic lapse rate dT/dp
      const TK = currentT + 273.15;
      const es = 6.112 * Math.exp((17.67 * currentT) / (currentT + 243.5));
      const ws = Math.max(0, (eps * es) / Math.max(1, p - es));

      const num = ((Rd * TK) / (cp * p)) * (1 + (Lv * ws) / (Rd * TK));
      const den = 1 + (Lv * Lv * ws * eps) / (cp * Rd * TK * TK);
      const dTdp = num / den;

      currentT -= dTdp * dp;
    }

    return { thetaK: theta, points };
  });
}

export interface MixingRatioCurve {
  mixingRatio: number; // g/kg
  points: Array<{ x: number; y: number }>;
}

/**
 * Mixing ratio lines (isohumes): lines of constant saturation mixing ratio w (g/kg).
 */
export function getMixingRatioLines(options: SkewTOptions): MixingRatioCurve[] {
  const ratios = [1, 2, 4, 8, 12, 16, 20];
  const { pMin, pMax } = options;
  const eps = 0.622;
  const dp = Math.max(10, Math.round((pMax - pMin) / 20));

  return ratios.map(w => {
    const points: Array<{ x: number; y: number }> = [];
    const wRatio = w / 1000;

    for (let p = pMax; p >= Math.max(400, pMin); p -= dp) {
      const es = (wRatio * p) / (eps + wRatio);
      if (es > 0 && es < p) {
        const logE = Math.log(es / 6.112);
        const denom = 17.67 - logE;
        if (denom > 0) {
          const tempC = (243.5 * logE) / denom;
          points.push({
            x: temperatureToX(tempC, p, options),
            y: pressureToY(p, options)
          });
        }
      }
    }
    return { mixingRatio: w, points };
  });
}

// ── Sensor Data Processing for Skew-T Sounding ────────────────────────────────

export interface RawMeasurement {
  timestamp: string;
  channel: string;
  value: number;
}

export interface RawCoordinate {
  latitude: number;
  longitude: number;
  altitude: number;
  timestamp: string;
}

/**
 * Processes and merges sensor readings into a chronological array of SkewT points
 * with pressure, temperature, dewpoint (if RH available), and GNSS altitude.
 */
/**
 * Processes and merges sensor readings into a chronological array of SkewT points
 * with pressure, temperature, dewpoint (if RH available), and GNSS altitude.
 * Strictly uses raw recorded sensor readings without any interpolation or averaging.
 * When humidity data is present, adheres to MetPy's `dropna(subset=['temperature', 'relativeHumidity', 'pressure'])`
 * so that every single plotted sounding point has genuine raw measurements for all channels.
 */
export function processSkewTData(
  measurements: RawMeasurement[] = [],
  coordinates: RawCoordinate[] = [],
  maxPoints: number = 700
): SkewTPoint[] {
  if (!measurements || measurements.length === 0) return [];

  // Group raw readings by 1-second timestamp without averaging or interpolation
  const timeMap = new Map<number, { p?: number; t?: number; rh?: number }>();
  let hasAnyRh = false;

  for (const m of measurements) {
    if (!m.timestamp || isNaN(m.value)) continue;
    const tsSec = Math.floor(new Date(m.timestamp).getTime() / 1000) * 1000;
    if (isNaN(tsSec)) continue;

    if (!timeMap.has(tsSec)) {
      timeMap.set(tsSec, {});
    }
    const entry = timeMap.get(tsSec)!;

    if (m.channel === 'pressure' && entry.p === undefined) {
      entry.p = m.value;
    } else if (m.channel === 'temperature' && entry.t === undefined) {
      entry.t = m.value;
    } else if (m.channel === 'relativeHumidity') {
      hasAnyRh = true;
      if (entry.rh === undefined) {
        entry.rh = m.value;
      }
    }
  }

  // Coordinate altitude map (matched by exact second, no interpolation)
  const altMap = new Map<number, number>();
  for (const c of coordinates) {
    if (!c.timestamp || isNaN(c.altitude)) continue;
    const tsSec = Math.floor(new Date(c.timestamp).getTime() / 1000) * 1000;
    if (!isNaN(tsSec) && !altMap.has(tsSec)) {
      altMap.set(tsSec, c.altitude);
    }
  }

  // Extract points where all required parameters are present (equivalent to df.dropna)
  const sortedTimes = Array.from(timeMap.keys()).sort((a, b) => a - b);
  const rawPoints: SkewTPoint[] = [];

  for (const ts of sortedTimes) {
    const entry = timeMap.get(ts)!;
    // Pressure and Temperature must be present
    if (entry.p === undefined || entry.t === undefined || isNaN(entry.p) || isNaN(entry.t)) {
      continue;
    }

    // If the flight recorded humidity data, require humidity for the point
    // (matches MetPy's df.dropna(subset=['temperature', 'relativeHumidity', 'pressure']))
    if (hasAnyRh && (entry.rh === undefined || isNaN(entry.rh))) {
      continue;
    }

    const dewpoint = entry.rh !== undefined ? calculateDewpoint(entry.t, entry.rh) : undefined;

    rawPoints.push({
      timestamp: ts,
      pressure: entry.p,
      temperature: entry.t,
      dewpoint,
      relativeHumidity: entry.rh,
      altitude: altMap.get(ts)
    });
  }

  if (rawPoints.length <= maxPoints) {
    return rawPoints;
  }

  // Downsample evenly across flight duration to guarantee smooth rendering
  const step = (rawPoints.length - 1) / (maxPoints - 1);
  const downsampled: SkewTPoint[] = [];
  for (let i = 0; i < maxPoints; i++) {
    const idx = Math.min(rawPoints.length - 1, Math.round(i * step));
    downsampled.push(rawPoints[idx]);
  }
  return downsampled;
}

/**
 * Calculates temperature bounds [tMin, tMax] so that all flight soundings (both temperature
 * and dewpoint) fit comfortably within the plot area, taking into account the 45° skew displacement
 * at each pressure level so curves never escape the right or left border.
 */
export function calculateFittedTemperatureBounds(
  points: SkewTPoint[],
  pMin: number,
  pMax: number,
  plotWidth: number,
  plotHeight: number,
  skewAngleDeg: number = 45,
  isFittedMode: boolean = false
): { tMin: number; tMax: number } {
  if (!points || points.length === 0) {
    return { tMin: -35, tMax: 35 };
  }

  const angleRad = (skewAngleDeg * Math.PI) / 180;
  const tanAngle = Math.tan(angleRad);
  const logPMax = Math.log(pMax);
  const logPMin = Math.log(pMin);
  const logDiff = Math.max(1e-4, logPMax - logPMin);

  // Usable padding on left and right edges (5% of plotWidth, at least 16px, max 40px)
  const padPx = Math.max(16, Math.min(40, plotWidth * 0.05));
  const minAllowedX = padPx;
  const maxAllowedX = plotWidth - padPx;

  // Extract all (temperature, skewPx) pairs
  const items: Array<{ temp: number; skewPx: number }> = [];
  let rawMinT = Infinity;
  let rawMaxT = -Infinity;

  for (const p of points) {
    const clampedP = Math.max(pMin, Math.min(pMax, p.pressure));
    const yNorm = (logPMax - Math.log(clampedP)) / logDiff;
    const skewPx = yNorm * plotHeight * tanAngle;

    items.push({ temp: p.temperature, skewPx });
    if (p.temperature < rawMinT) rawMinT = p.temperature;
    if (p.temperature > rawMaxT) rawMaxT = p.temperature;

    if (p.dewpoint !== undefined && !isNaN(p.dewpoint)) {
      items.push({ temp: p.dewpoint, skewPx });
      if (p.dewpoint < rawMinT) rawMinT = p.dewpoint;
      if (p.dewpoint > rawMaxT) rawMaxT = p.dewpoint;
    }
  }

  if (items.length === 0) {
    return { tMin: -35, tMax: 35 };
  }

  // Helper to compute min and max screen X coordinates for a candidate [tMin, tMax]
  const evalXRange = (candTMin: number, candTMax: number): { minX: number; maxX: number } => {
    const deltaT = Math.max(1e-4, candTMax - candTMin);
    let minX = Infinity;
    let maxX = -Infinity;
    for (const item of items) {
      const x = ((item.temp - candTMin) / deltaT) * plotWidth + item.skewPx;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
    }
    return { minX, maxX };
  };

  // Initial candidate bounds rounded to multiples of 5
  let tMax = Math.ceil((rawMaxT + 2) / 5) * 5;
  let tMin = Math.floor((rawMinT - 2) / 5) * 5;

  if (!isFittedMode) {
    tMax = Math.max(tMax, 35);
    tMin = Math.min(tMin, -30);
  }

  // Ensure minimum initial span
  const minSpan = isFittedMode ? 25 : 65;
  if (tMax - tMin < minSpan) {
    tMax = tMin + minSpan;
  }

  // Iteratively expand until all points strictly fit within [minAllowedX, maxAllowedX]
  for (let iter = 0; iter < 50; iter++) {
    const { minX, maxX } = evalXRange(tMin, tMax);

    if (minX >= minAllowedX && maxX <= maxAllowedX) {
      break;
    }

    if (maxX > maxAllowedX) {
      // Points exceed right edge: expand tMax to pull them left
      tMax += 5;
    }
    if (minX < minAllowedX) {
      // Points exceed left edge: decrease tMin to push them right
      tMin -= 5;
    }
  }

  // In standard mode, preserve classic meteorological boundaries if data permits
  if (!isFittedMode) {
    if (tMin > -30) tMin = -30;
    if (tMax < 35) tMax = 35;
    while (evalXRange(tMin, tMax).maxX > maxAllowedX) {
      tMax += 5;
    }
    while (evalXRange(tMin, tMax).minX < minAllowedX) {
      tMin -= 5;
    }
  }

  return { tMin, tMax };
}

export interface DashedSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  factor: number;
}

/**
 * Generates discrete dashed line segments along a polyline.
 * This guarantees true, physical dashed lines with per-segment color interpolation,
 * working reliably in all browsers without relying on SVG masks.
 */
export function generateDashedSegments(
  points: { x: number; y: number; factor: number }[],
  dashLength: number = 6,
  gapLength: number = 4
): DashedSegment[] {
  if (!points || points.length < 2) return [];

  const segments: DashedSegment[] = [];
  let isDash = true;
  let remainingInCurrentState = dashLength;
  let currentStart = { ...points[0] };

  for (let i = 0; i < points.length - 1; i++) {
    const pA = points[i];
    const pB = points[i + 1];

    const segDx = pB.x - pA.x;
    const segDy = pB.y - pA.y;
    const segDist = Math.hypot(segDx, segDy);

    if (segDist <= 0.0001) continue;

    let traveledOnSeg = 0;

    while (traveledOnSeg < segDist) {
      const distanceLeftOnSeg = segDist - traveledOnSeg;

      if (distanceLeftOnSeg <= remainingInCurrentState) {
        // Can reach the end of this point segment without toggling state
        traveledOnSeg = segDist;
        remainingInCurrentState -= distanceLeftOnSeg;

        if (isDash) {
          segments.push({
            x1: currentStart.x,
            y1: currentStart.y,
            x2: pB.x,
            y2: pB.y,
            factor: (currentStart.factor + pB.factor) / 2
          });
        }

        currentStart = { ...pB };

        if (remainingInCurrentState <= 0.0001) {
          isDash = !isDash;
          remainingInCurrentState = isDash ? dashLength : gapLength;
        }
      } else {
        // State toggles within this segment
        const distFromA = traveledOnSeg + remainingInCurrentState;
        const ratio = distFromA / segDist;
        const nextX = pA.x + ratio * segDx;
        const nextY = pA.y + ratio * segDy;
        const nextFactor = pA.factor + ratio * (pB.factor - pA.factor);

        if (isDash) {
          segments.push({
            x1: currentStart.x,
            y1: currentStart.y,
            x2: nextX,
            y2: nextY,
            factor: (currentStart.factor + nextFactor) / 2
          });
        }

        traveledOnSeg += remainingInCurrentState;
        currentStart = { x: nextX, y: nextY, factor: nextFactor };
        isDash = !isDash;
        remainingInCurrentState = isDash ? dashLength : gapLength;
      }
    }
  }

  return segments;
}

